/**
 * Food Hygiene Rating Badge Component
 *
 * Renders the official UK Food Standards Agency (FSA) Food Hygiene Rating
 * badge in the standard format.
 */

import type { FoodHygieneRating } from "@hsh/shared";

interface FoodHygieneRatingBadgeProps {
  rating: FoodHygieneRating;
  size?: "small" | "medium" | "large";
  showDetails?: boolean;
}

// Rating to label mapping
const RATING_LABELS: Record<string, string> = {
  "5": "VERY GOOD",
  "4": "GOOD",
  "3": "GENERALLY SATISFACTORY",
  "2": "IMPROVEMENT NECESSARY",
  "1": "MAJOR IMPROVEMENT NECESSARY",
  "0": "URGENT IMPROVEMENT NECESSARY",
  Pass: "PASS",
  "Improvement Required": "IMPROVEMENT REQUIRED",
  Exempt: "EXEMPT",
  AwaitingInspection: "AWAITING INSPECTION",
};

// Rating to background color
const RATING_COLORS: Record<string, string> = {
  "5": "#7AB41D", // Green
  "4": "#7AB41D",
  "3": "#7AB41D",
  "2": "#E2A615", // Amber/warning
  "1": "#E2A615",
  "0": "#D22630", // Red
  Pass: "#7AB41D",
  "Improvement Required": "#E2A615",
  Exempt: "#6B7280",
  AwaitingInspection: "#6B7280",
};

export default function FoodHygieneRatingBadge({
  rating,
  size = "medium",
  showDetails = true,
}: FoodHygieneRatingBadgeProps) {
  const isNumericRating =
    rating.ratingNumeric !== undefined && rating.ratingNumeric !== null;
  const ratingValue = rating.rating;
  const bgColor = RATING_COLORS[ratingValue] || "#6B7280";
  const label = RATING_LABELS[ratingValue] || ratingValue;

  // Size configurations - made wider to prevent text wrapping
  const sizeConfig = {
    small: {
      container: "w-52",
      title: "text-xs",
      ratingCircle: "w-6 h-6 text-xs",
      selectedCircle: "w-8 h-8 text-base",
      labelText: "text-xs",
      spacing: "gap-1",
    },
    medium: {
      container: "w-64",
      title: "text-sm",
      ratingCircle: "w-7 h-7 text-sm",
      selectedCircle: "w-10 h-10 text-xl",
      labelText: "text-sm",
      spacing: "gap-1",
    },
    large: {
      container: "w-80",
      title: "text-base",
      ratingCircle: "w-8 h-8 text-base",
      selectedCircle: "w-12 h-12 text-2xl",
      labelText: "text-base",
      spacing: "gap-1.5",
    },
  };

  const cfg = sizeConfig[size];

  // For numeric ratings (England, Wales, NI), show the 0-5 scale
  if (isNumericRating) {
    const numRating = rating.ratingNumeric!;

    return (
      <div className="space-y-2">
        {/* Main Badge */}
        <div
          className={`${cfg.container} rounded-lg overflow-hidden shadow-md`}
          style={{ backgroundColor: bgColor }}
        >
          {/* Header - black text on green */}
          <div className="px-3 py-2 text-center">
            <span
              className={`font-bold text-black ${cfg.title}`}
              style={{ letterSpacing: "0.05em" }}
            >
              FOOD HYGIENE RATING
            </span>
          </div>

          {/* Rating circles - all 6 shown, selected one is bigger and filled black */}
          <div className="px-3 py-2">
            <div className={`flex items-center justify-center ${cfg.spacing}`}>
              {[0, 1, 2, 3, 4, 5].map((n) => {
                const isSelected = n === numRating;
                return (
                  <div
                    key={n}
                    className={`rounded-full flex items-center justify-center font-bold
                      ${
                        isSelected
                          ? `${cfg.selectedCircle} bg-black text-white`
                          : `${cfg.ratingCircle} border-2 border-black/70 text-black/70 bg-transparent`
                      }`}
                  >
                    {n}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Label - black background, white text */}
          <div
            className="py-2 text-center"
            style={{ backgroundColor: "#000000" }}
          >
            <span
              className={`font-bold text-white ${cfg.labelText}`}
              style={{ letterSpacing: "0.05em" }}
            >
              {label}
            </span>
          </div>
        </div>

        {/* Details (optional) */}
        {showDetails && (
          <div className="text-xs text-gray-500 space-y-1">
            {rating.ratingDate && rating.rating !== "AwaitingInspection" && (
              <div>
                Inspected:{" "}
                {new Date(rating.ratingDate).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </div>
            )}
            {rating.localAuthority && (
              <div>Authority: {rating.localAuthority}</div>
            )}
            {rating.newRatingPending && (
              <div className="text-amber-600 font-medium">
                ⚠️ New rating pending
              </div>
            )}
            {rating.fhrsUrl && (
              <a
                href={rating.fhrsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
              >
                View on food.gov.uk
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            )}
          </div>
        )}
      </div>
    );
  }

  // For Scotland or special ratings (Pass/Improvement Required/Exempt/Awaiting)
  return (
    <div className="space-y-2">
      <div
        className={`${cfg.container} rounded-lg overflow-hidden shadow-md`}
        style={{ backgroundColor: bgColor }}
      >
        {/* Header */}
        <div className="px-3 py-1.5 text-center">
          <span
            className={`font-bold text-white ${cfg.title}`}
            style={{ letterSpacing: "0.05em" }}
          >
            FOOD HYGIENE RATING
          </span>
        </div>

        {/* Rating text */}
        <div className="bg-white/10 px-3 py-3 text-center">
          <span
            className={`font-bold text-white ${
              size === "large"
                ? "text-xl"
                : size === "medium"
                ? "text-lg"
                : "text-base"
            }`}
          >
            {label}
          </span>
        </div>
      </div>

      {/* Details */}
      {showDetails && (
        <div className="text-xs text-gray-500 space-y-1">
          {rating.ratingDate && rating.rating !== "AwaitingInspection" && (
            <div>
              Inspected:{" "}
              {new Date(rating.ratingDate).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </div>
          )}
          {rating.localAuthority && (
            <div>Authority: {rating.localAuthority}</div>
          )}
          {rating.fhrsUrl && (
            <a
              href={rating.fhrsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
            >
              View on food.gov.uk
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact inline version showing just the rating number with color
 */
export function FoodHygieneRatingInline({
  rating,
}: {
  rating: FoodHygieneRating;
}) {
  const ratingValue = rating.rating;
  const bgColor = RATING_COLORS[ratingValue] || "#6B7280";
  const isNumeric =
    rating.ratingNumeric !== undefined && rating.ratingNumeric !== null;

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-white text-xs font-bold"
      style={{ backgroundColor: bgColor }}
      title={`Food Hygiene Rating: ${ratingValue}`}
    >
      {isNumeric ? (
        <>
          <span>🍽️</span>
          <span>{rating.ratingNumeric}/5</span>
        </>
      ) : (
        <span>{ratingValue}</span>
      )}
    </span>
  );
}
